import { LinkGuideLayout } from '../components/LinkGuideLayout';

const sections = [
  ['connection', 'Check a saved MyChron before changing settings'],
  ['mesh-and-router', 'Mesh, guest Wi-Fi and a different router'],
  ['network-change', 'Finish a network change'],
  ['updates', 'Update Link without losing saved work'],
  ['update-errors', 'Recover a failed update'],
  ['status', 'Check connection and session status'],
] as const;

export function PaddiqLinkTroubleshootingPage() {
  return (
    <LinkGuideLayout
      path="/resources/paddiq-link-troubleshooting"
      title="Update and troubleshoot Paddiq Link"
      lead="Identify whether a problem affects logger Wi-Fi, account access, downloads or uploads, and keep your saved work while recovering."
      sections={sections}
    >
      <section className="guide-step" id="connection">
        <div>
          <h2>Check a saved MyChron before changing settings.</h2>
          <p>
            Keep the computer and logger on their intended network. Close other Link or Race Studio
            connections to this MyChron. On the saved MyChron, use <strong>Reconnect</strong> or{' '}
            <strong>Connection help → Retry connection</strong>. This verifies the logger without
            sending new Wi-Fi settings. A saved address, old online indicator or shared workspace
            record is not a fresh local connection.
          </p>
          <p>
            <strong>Connection help</strong> is available for saved MyChrons. When a check fails,
            choose what the MyChron shows: connected to Wi-Fi, Hotspot / Access Point, still
            joining, or frozen on its startup screen. Link gives the matching next step and checks
            the same logger before continuing. A failed settings save keeps your entered details;
            checking a joined logger does not send its settings again. If it is still joining,
            restart it once after Link’s current operation finishes. If it stays on its startup logo
            after that, close setup and contact support. Keep the MyChron saved in Link.
          </p>
          <p>
            If the logger is in hotspot mode, join its exact hotspot on this computer and choose{' '}
            <strong>Check connection</strong>. You can keep it in hotspot mode;{' '}
            <strong>Connect to paddock Wi-Fi</strong> is optional. Follow the{' '}
            <a href="/resources/paddiq-link#with-link">saved-logger setup steps</a> when
            deliberately changing modes.
          </p>
          <p>
            For a logger you have not saved, use{' '}
            <a href="/resources/paddiq-link#lan">Find MyChrons already on the LAN</a> and verify its
            identity before adding it. Do not remove and re-add a saved logger as a connection
            retry.
          </p>
        </div>
      </section>
      <section className="guide-step" id="mesh-and-router">
        <div>
          <h2>Mesh, guest Wi-Fi and a different router.</h2>
          <p>
            A mesh network can work when its access points provide a local network that allows the
            computer and MyChron to communicate. Being listed as connected in the router, having
            internet access, or seeing the same Wi-Fi name does not prove that this local connection
            is allowed. Guest networks, client isolation and separate network segments can block it.
          </p>
          <ol className="guide-instructions">
            <li>
              Check the MyChron’s displayed network and IP address, then check the computer’s actual
              Wi-Fi connection. Keep both on the intended paddock LAN.
            </li>
            <li>
              Check the router’s guest-network and device-isolation settings. Prefer a secured
              paddock network that allows local device communication. A second computer failing too
              is useful evidence, but does not identify the cause by itself.
            </li>
            <li>
              Close other Link or Race Studio connections to this logger while checking it. Use{' '}
              <strong>Connection help</strong> and <strong>Retry connection</strong>. A saved
              address or old online indicator is not a fresh successful check.
            </li>
            <li>
              If you changed routers or Wi-Fi credentials, choose{' '}
              <strong>Move to another network</strong> on the saved MyChron. Select whether it is on
              the old Wi-Fi, in hotspot mode, or already on the new Wi-Fi, then follow the matching
              verification steps.
            </li>
          </ol>
          <p>
            Keep the saved logger: Link verifies its identity even when DHCP gives it a different
            address. A new router can also create a different paddock context, even if you reused
            the Wi-Fi name. Confirm the new paddock and its circuit before resuming. A successful
            connection on another router establishes that path works; it does not prove what failed
            on the previous network.
          </p>
        </div>
      </section>
      <section className="guide-step" id="network-change">
        <div>
          <h2>Finish a network change.</h2>
          <p>
            Use <strong>Move to another network</strong> for a saved MyChron. Choose where it is
            now: still on the old Wi-Fi, already in hotspot mode, or already on the new Wi-Fi. Link
            can restore its saved hotspot first, continue after a manual reset, or verify settings
            changed in another app. Join the relevant Wi-Fi on this computer when prompted. Link
            verifies the same MyChron before continuing; its driver, kart, history, and sync
            switches stay saved. The fleet can show the current network, all saved MyChrons, or a
            specific saved network.
          </p>
          <p>
            If the handoff fails, read the logger’s display. For <strong>NWK JOINED</strong>,
            reconnect the computer to the named paddock network and choose{' '}
            <strong>Verify pending paddock connection</strong>. When adding a MyChron, choose{' '}
            <strong>Verify saved settings</strong> if offered. Verification retries the connection
            without sending the Wi-Fi settings again. For hotspot/AP, rejoin the logger’s hotspot,
            verify its identity again and retry with corrected settings. Keep the saved record and
            local recovery data.
          </p>
          <p>
            Changing a preferred network in saved settings is separate from applying it. Follow{' '}
            <a href="/resources/paddiq-link#with-link">the guarded Wi-Fi change</a> or{' '}
            <a href="/resources/paddiq-link#hotspot">restore hotspot instructions</a>. Keep the app
            open after a settings write starts. A configuration reset can change saved settings; it
            is not a simple mode toggle.
          </p>
        </div>
      </section>
      <section className="guide-step" id="updates">
        <div>
          <h2>Update Link without losing saved work.</h2>
          <p>
            Open <strong>Paddiq Link updates</strong> at the bottom of the sidebar and check the
            installed version and environment. <strong>Check for updates</strong> starts an optional
            check; you can close that dialog, including with Escape, while the check runs. Repeated
            activation does not start duplicate checks.
          </p>
          <ol className="guide-instructions">
            <li>
              A successful update check can start downloading automatically. If{' '}
              <strong>Download update</strong> is offered, use it, then choose{' '}
              <strong>Restart and update</strong> when ready. A required-update dialog remains open
              until the update is handled.
            </li>
            <li>
              Keep the app open while it waits for active transfers and saved changes. If it reports
              that work has not finished, let that work complete and retry. Do not force-quit during
              a logger settings operation or transfer.
            </li>
            <li>
              After restart, confirm the version, workspace and saved MyChrons. Check their
              individual sync switches and the global pause/resume state before starting the next
              session check.
            </li>
          </ol>
          <p>
            Older builds without an updater need a manual installation from the official download
            page. Install the matching newer app over the existing app in the same environment; keep
            its local data. An update error is not a reason to remove the saved logger, delete
            queued files, or use the other environment’s installer. Follow the displayed signing and
            operating-system guidance.
          </p>
        </div>
      </section>
      <section className="guide-step" id="update-errors">
        <div>
          <h2>Recover a failed update.</h2>
          <p>
            Read the update message, restore internet access if needed and use{' '}
            <strong>Retry update</strong> when offered. A required update remains required after an
            error; an optional check can be closed while it runs. Keep your saved MyChrons, local
            originals and queue.
          </p>
          <p>
            If Link is waiting for active work before restarting, let that work finish and retry.
            For a manual installation, choose the matching newer app from{' '}
            <a href="/paddiq-link">Paddiq Link downloads</a> and install it over the same
            environment. Follow the operating system’s signing guidance. Contact support if
            installation still fails.
          </p>
        </div>
      </section>
      <section className="guide-step" id="status">
        <div>
          <h2>Check connection and session status.</h2>
          <p>
            An online logger is reachable locally; that does not mean every session is uploaded.
            Searching means discovery is underway. Offline can mean the logger is off, out of range,
            on another network, or blocked by local-network restrictions. A shared workspace record
            alone does not prove this computer can reach the logger.
          </p>
          <p>
            If no saved loggers are detected after the initial scan, check whether this computer is
            on the same paddock network as the MyChrons. Treat this as troubleshooting, not a
            diagnosis: the devices could also be off. If one logger is online, inspect the other
            logger’s individual status rather than changing the whole fleet.
          </p>
          <p>
            Pending or queued work may still be waiting to download from the MyChron. Check the
            transfer stage before assuming the original is stored on this computer. For a downloaded
            file waiting to upload, restore internet access, confirm the workspace and required
            driver/kart/circuit context, and inspect <strong>Transfers</strong> for errors. Do not
            delete the local queue to clear an error. <strong>Last synced</strong> records the last
            successful session-list check, even when no new files need uploading. An online logger
            can show <strong>Never synced</strong> before that first check. Check each session's
            upload status separately.
          </p>
          <p>
            On Windows, if Link cannot read the Wi-Fi name, check Location services and desktop-app
            location access, then confirm the network manually. On Mac, check the app’s
            local-network permission when prompted. Neither permission replaces a router rule that
            allows local communication.
          </p>
          <ul>
            <li>
              <strong>Online</strong>: Link has verified the saved logger locally.
            </li>
            <li>
              <strong>Reading / Downloading</strong>: Link is checking or copying logger files.
            </li>
            <li>
              <strong>Queued</strong>: work is pending; inspect whether it still needs the MyChron,
              a download, assignment context or cloud access.
            </li>
            <li>
              <strong>Uploading</strong>: Link is sending the file to Paddiq.
            </li>
            <li>
              <strong>Open in Paddiq</strong>: open the completed upload to verify its session and
              assignment.
            </li>
          </ul>
          <p>
            Keep Link running for pending uploads. Closing its window leaves it in the background;
            quitting stops that work. A cloud outage does not require changing logger Wi-Fi. A
            logger connection failure does not require deleting cloud sessions. Use the action for
            the failing stage and keep the local originals.
          </p>
          <p>
            For a beta problem, <a href="/support">report it to Paddiq support</a> with the Link
            version, computer OS, logger identity/model, displayed network mode and exact error.
            Include whether it affects discovery, reading, uploading or circuit loading, and whether
            it started after a router, account or app change. Do not include passwords, tokens or
            raw customer session files.
          </p>
        </div>
      </section>
    </LinkGuideLayout>
  );
}
